import type { ReactElement } from 'react';

import type { CrmObject } from '../../core/types';
import type { HeaderActionDefinition } from '../registry/entityTypeRegistry';

export interface HeaderActionsProps {
  /** Action definitions from the entity-type registry (module-scope, stable reference). */
  actions: HeaderActionDefinition[];
  object: CrmObject;
}

// Ghost icon button shared by the header action strip (mail / brand-link
// icons / kebab). Borderless with a subtle hover fill — bordered+shadowed
// "chips" read as clunky outlined squares next to the heading. The larger
// `p-2` keeps a comfortable ~36px tap target, and `text-fg-quaternary` keeps
// the stroke glyphs subdued so they don't fight the title.
// Exported so `CRMObjectFullPageView` and `RecordActionMenu` can render peer
// icon buttons in the same row without the styling drifting between files.
export const ACTION_BUTTON_CLASS =
  'inline-flex items-center justify-center p-2 rounded-lg text-fg-quaternary hover:bg-[var(--color-bg-secondary)] hover:text-fg-secondary transition-colors cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring';

// Overrides applied to lib/base `<Button size='sm'>` so labeled toolbar
// buttons (Send email, the Activity filter) sit at the slim tab/icon scale
// rather than the default chunky `sm` (py-2 + 20px icon + semibold). Shared so
// every top-bar labeled button stays visually identical. Icons are 12px for a
// delicate top bar (down from the original chunky 20px).
export const COMPACT_TOOLBAR_BUTTON_CLASS =
  '!py-1.5 !font-medium [&_svg]:!size-3';

/**
 * Renders entity-type header action buttons (Gmail / LinkedIn / Website, etc.).
 * Each action's href is computed from the object at render time; actions whose
 * `guard` fails or whose `getHref` returns null are not rendered.
 */
export function HeaderActions({ actions, object }: HeaderActionsProps) {
  return actions
    .map(action => {
      if (action.guard && !action.guard(object)) {
        return null;
      }
      const href = action.getHref(object);
      if (!href) {
        return null;
      }
      const Icon = action.icon;
      return (
        <a
          key={action.id}
          href={href}
          target='_blank'
          rel='noopener noreferrer'
          aria-label={action.getLabel(object)}
          className={ACTION_BUTTON_CLASS}
          data-component='external-link-button'
          data-instance={action.id}
        >
          <Icon className='size-3' aria-hidden='true' />
        </a>
      );
    })
    .filter((el): el is ReactElement => el !== null);
}
